import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';

class CartDiscounts extends HTMLElement {
  connectedCallback() {
    // Get references to elements
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="discount-code"]');
    this.submitBtn = this.querySelector('button[type="button"]');
    this.submitBtnSpan = this.submitBtn?.querySelector('span');
    this.errorMessage = this.querySelector('[ref="discountErrorMessage"]');
    
    // Clean discount from URL on load
    this.cleanDiscountFromURL();
    
    // Log current cart discounts on page load
    this.logCurrentDiscounts();
    
    // Bind event listeners
    if (this.form && this.submitBtn) {
      this.submitBtn.addEventListener('click', this.applyDiscount);
    }
    
    // Bind remove discount listeners using event delegation
    document.addEventListener('click', this.handleRemoveDiscount);
  }

  cleanDiscountFromURL() {
    const url = new URL(window.location.href);
    if (url.searchParams.has('discount')) {
      url.searchParams.delete('discount');
      window.history.replaceState({}, '', url.toString());
    }
  }

  async logCurrentDiscounts() {
    // Method kept for potential future debugging
  }

  applyDiscount = async (e) => {
    e.preventDefault();
    
    // Get the discount code value
    const discountCode = this.input.value.trim();
    
    // Validate input
    if (!discountCode) {
      return;
    }
    
    // Hide any previous error messages
    if (this.errorMessage) {
      this.errorMessage.classList.add('hidden');
    }
    
    // Disable button during request
    this.submitBtn.disabled = true;
    if (this.submitBtnSpan) {
      this.submitBtnSpan.textContent = 'Applying...';
    }

    try {
      // Send request to Shopify
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: discountCode
        })
      });
      
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      
      const cart = await response.json();

      const hasApplicableDiscounts = cart.discount_codes?.some(d => d.applicable === true);
      const hasCartLevelDiscounts = cart.cart_level_discount_applications?.length > 0;
      const discountApplied = hasApplicableDiscounts || hasCartLevelDiscounts;
      
      if (discountApplied) {
        // Success!
        this.input.value = ''; // Clear the input
        window.__cart = cart; // Update global cart
        
        // Fetch and update the cart section without reloading
        await this.refreshCartSection();
        
        // Dispatch event to update other components (like cart bar)
        ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
        
        // Re-enable button after success
        this.submitBtn.disabled = false;
        if (this.submitBtnSpan) {
          this.submitBtnSpan.textContent = 'Apply';
        }
      } else {
        // Discount code invalid or not applicable
        this.showError('Invalid or expired discount code');
        
        // Re-enable button after error
        this.submitBtn.disabled = false;
        if (this.submitBtnSpan) {
          this.submitBtnSpan.textContent = 'Apply';
        }
      }

    } catch (error) {
      console.error('Error applying discount:', error);
      this.showError('Something went wrong. Please try again.');
      
      // Re-enable button after exception
      this.submitBtn.disabled = false;
      if (this.submitBtnSpan) {
        this.submitBtnSpan.textContent = 'Apply';
      }
    }
  };

  async refreshCartSection() {
    try {
      // Determine if we're on cart page or drawer
      const isDrawer = !!document.querySelector('cart-drawer');
      const sectionName = isDrawer ? 'cart-drawer' : 'cart';
      
      // Fetch the updated section HTML
      const response = await fetch(`/?sections=${sectionName}`);
      const data = await response.json();
      const html = data[sectionName];
      
      if (!html) return;
      
      // Parse the new HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector(`#shopify-section-${sectionName}`);
      const currentSection = document.querySelector(`#shopify-section-${sectionName}`);
      
      if (newSection && currentSection) {
        // Replace the section content
        currentSection.innerHTML = newSection.innerHTML;
      }
      
      // Remove discount parameter from URL if present
      const url = new URL(window.location.href);
      if (url.searchParams.has('discount')) {
        url.searchParams.delete('discount');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (error) {
      console.error('Error refreshing cart section:', error);
    }
  }

  showError(message) {
    if (!this.errorMessage) {
      return;
    }
    
    this.errorMessage.textContent = message;
    this.errorMessage.classList.remove('hidden');
    
    // Hide after 3 seconds
    setTimeout(() => {
      this.errorMessage.classList.add('hidden');
    }, 3000);
  }

  handleRemoveDiscount = async (e) => {
    const removeBtn = e.target.closest('.discount-remove-btn');
    if (!removeBtn) return;
    
    e.preventDefault();
    const discountCode = removeBtn.dataset.discountCode;
    
    if (!discountCode) return;
    
    try {
      // First get the current cart to see all discount codes
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();
      
      // Get all current discount codes
      const currentCodes = currentCart.discount_codes?.map(d => d.code) || [];
      
      // Remove the specific code
      const remainingCodes = currentCodes.filter(code => code !== discountCode);
      
      // Update cart with remaining discount codes
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: remainingCodes.join(',') // Send remaining codes (empty string if none)
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to remove discount: ${response.status}`);
      }
      
      const cart = await response.json();
      
      window.__cart = cart;
      
      // Refresh the cart section
      await this.refreshCartSection();
      
      // Dispatch event to update other components
      ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
    } catch (error) {
      console.error('Error removing discount:', error);
      alert('Failed to remove discount. Please try again.');
    }
  };

  disconnectedCallback() {
    // Clean up event listeners
    if (this.submitBtn) {
      this.submitBtn.removeEventListener('click', this.applyDiscount);
    }
    document.removeEventListener('click', this.handleRemoveDiscount);
  }
}

// Register the custom element
customElements.define('cart-discounts', CartDiscounts);
